// ============================================================
// THE MOVE'S BEAT FACTS - who moved, from where to where, by which route, at
// what pace (plan design-gaps, M1).
//
// A move is an EVENT ON THE LOG, not a token field: the token record stays the
// committed position (a field `setBattlefieldToken` does not list is erased
// on echo, and a stored route would replay on every reload), and the log is
// the stream every screen already reads once, with high-water marks and a
// veil at the sync boundary. The mover's client writes one public `info`
// line with this in its `beat` column (text, never json - the echo
// prevention compares strings), and each screen derives the walk from it
// (engine/display/walk). A hidden figure's move is never written: its token
// never reaches the players, and neither may its route.
//
// Pure. Zero React dependency.
// ============================================================
import type { NarrativeEntry, Tab } from '../types';
import { MOVEMENT_MODES, type MovementMode } from './battlefield/movement';
import { parseBeat } from './attackBeat';
import { collectFromLogs, type LogLine, type LogMarks } from './log/collect';

/**
 * The action the move was made under: what sets its pace on the display. Auto is resolved by the writer. `crawl`
 * is a Pinned figure's crawl to cover (2026-10-25) - a line word only, never a movement band.
 */
export type MoveMode = MovementMode | 'crawl';

const MOVE_MODES: readonly string[] = [...MOVEMENT_MODES, 'crawl'];

/** A point of the route, in metres; `y` the height walked at, absent on flat ground. */
export interface MovePoint {
  x: number;
  z: number;
  y?: number;
}

export interface MoveBeat {
  actorId: string;
  /** When the move was made, on the screen that made it: with the actor, what makes two copies one move. */
  at: number;
  from: MovePoint;
  to: MovePoint;
  /** The route's points between `from` and `to`, in order; empty for a straight line. */
  path: MovePoint[];
  mode: MoveMode;
  /** The ground the move cost, in metres, as the route's label said. */
  metres: number;
}

/** A move as a screen collects it: the beat under its id. */
export interface Move extends MoveBeat {
  /** `${actorId}:${at}` - the same move seen twice is one. */
  id: string;
}

/** A route longer than this is thinned to it: the column is a line of the log, not a track recording. */
const MAX_PATH_POINTS = 48;

/** To a tenth of a metre: the walk needs no finer, the column stays short. */
const tenth = (v: number) => Math.round(v * 10) / 10;

const point = (p: MovePoint): MovePoint => ({ x: tenth(p.x), z: tenth(p.z), ...(p.y ? { y: tenth(p.y) } : {}) });

/** A turn in the route: the points either side are not in line through it (the cross product, to a hair). */
const isCorner = (a: MovePoint, b: MovePoint, c: MovePoint) =>
  Math.abs((b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x)) > 1e-6;

/**
 * The route within the cap, its shape kept: the points along a straight run go first (a run needs only its ends),
 * and only a route still over the cap after that is subsampled - its ends always kept.
 */
function thinned(path: readonly MovePoint[]): MovePoint[] {
  if (path.length <= MAX_PATH_POINTS) return [...path];
  const last = path.length - 1;
  const corners = path.filter((p, i) => i === 0 || i === last || isCorner(path[i - 1], p, path[i + 1]));
  if (corners.length <= MAX_PATH_POINTS) return corners;
  const every = Math.ceil(corners.length / MAX_PATH_POINTS);
  return corners.filter((_, i) => i % every === 0 || i === corners.length - 1);
}

/** The column's value for a move line. */
export function moveBeat(m: MoveBeat): string {
  return JSON.stringify({
    move: true,
    actorId: m.actorId,
    at: m.at,
    mode: m.mode,
    metres: tenth(m.metres),
    from: point(m.from),
    to: point(m.to),
    path: thinned(m.path).map(point),
  });
}

const isPoint = (v: unknown): v is MovePoint =>
  !!v && typeof v === 'object' && typeof (v as MovePoint).x === 'number' && typeof (v as MovePoint).z === 'number';

const readPoint = (p: MovePoint): MovePoint => ({ x: p.x, z: p.z, ...(typeof p.y === 'number' ? { y: p.y } : {}) });

/** The facts off a move line's column; null for anything else (an attack's, a reveal's, nothing). */
export function readMoveBeat(value: unknown): MoveBeat | null {
  const v = parseBeat(value);
  if (!v || v.move !== true) return null;
  const { actorId, at, mode, metres, from, to, path } = v;
  if (typeof actorId !== 'string' || typeof at !== 'number' || !isPoint(from) || !isPoint(to)) return null;
  return {
    actorId,
    at,
    from: readPoint(from),
    to: readPoint(to),
    path: Array.isArray(path) ? path.filter(isPoint).map(readPoint) : [],
    mode: MOVE_MODES.includes(mode as string) ? (mode as MoveMode) : 'half',
    metres: typeof metres === 'number' ? metres : 0,
  };
}

/**
 * Is this log line's beat the move of `actorId` made at `at` - one run's own line, and nothing else (an undone
 * charge deletes exactly it, 2026-10-21)? A beat is unique to an actor and a millisecond.
 */
export function isMoveLineOf(beat: unknown, line: { actorId: string; at: number }): boolean {
  const m = readMoveBeat(beat);
  return !!m && m.actorId === line.actorId && m.at === line.at;
}

/** The move a line describes, under its id; null for any other line. */
export function moveOf(entry: LogLine): Move | null {
  const beat = readMoveBeat(entry.beat);
  return beat && { ...beat, id: `${beat.actorId}:${beat.at}` };
}

/** Every move newer than the marks, from the tabs' logs and the shared one, each once (engine/log/collect). */
export function collectMoves(
  tabs: readonly Tab[],
  shared: readonly LogLine[],
  marks: LogMarks,
  now: number,
): { moves: Move[]; marks: LogMarks } {
  const { found, marks: next } = collectFromLogs(tabs, shared, marks, now, { local: moveOf, shared: moveOf });
  return { moves: found, marks: next };
}

/** The action's word on the line, as the route's label names it. */
const MODE_WORDS: Record<MoveMode, string> = {
  half: 'Half move',
  full: 'Full move',
  charge: 'Charge',
  run: 'Run',
  crawl: 'Crawl (pinned)',
};

/** "🚶 Castiel moves 12.4 m · Run": the public line, in the design canvas's words. */
export const moveLine = (name: string, beat: MoveBeat): string =>
  `🚶 ${name} moves ${beat.metres.toFixed(1)} m · ${MODE_WORDS[beat.mode]}`;

/** The line the mover's client logs: public, in the combat lane, its column the beat. */
export function moveEntry(name: string, beat: MoveBeat): NarrativeEntry {
  return { type: 'info', text: moveLine(name, beat), timestamp: beat.at, beat: moveBeat(beat) };
}
